import type { SafeParseReturnType } from 'zod';
import { CATEGORIES } from '@/core/config/categories';
import { isKeyOf } from '@/core/utils/object';
import { getLocaleTimeString } from '@/core/utils/time.client';
import { type Event, EventSchema } from '@/events/events/event.schema';
import {
	type Performer,
	PerformerSchema,
} from '@/events/performers/performer.schema';
import { type Venue, VenueSchema } from '@/events/venues/venue.schema';
import { type SearchResult, SearchResultSchema } from './search.schema';

function transform<T extends Event | Performer | Venue>(
	transformer: () => SafeParseReturnType<T, SearchResult>,
): SearchResult | null {
	const transformed = transformer();

	return transformed.success ? transformed.data : null;
}

export function transformEventToSearchResult(event: Event) {
	return transform<Event>(() =>
		EventSchema.transform((data) => ({
			assets: { listing: null, thumbnail: null },
			date: data.date,
			emoji: data.performers?.[0]?.emoji,
			image: null,
			price: data.price.min.formatted,
			subtitle: [
				getLocaleTimeString(data.date),
				data.venue?.name,
				[data.venue?.city, data.venue?.state].filter(Boolean).join(', '),
			]
				.filter(Boolean)
				.join(' • '),
			title: data.name,
			url: data.url,
		}))
			.pipe(SearchResultSchema)
			.safeParse(event),
	);
}

function resolveSubTitle(subject: string | undefined): string {
	if (!subject) return '';
	if (!isKeyOf(CATEGORIES, subject)) return '';

	return CATEGORIES[subject].title.trim();
}

export function transformPerformerToSearchResult(performer: Performer) {
	return transform<Performer>(() =>
		PerformerSchema.transform((data) => ({
			assets: data.assets,
			emoji: data.emoji,
			subtitle: resolveSubTitle(data.category),
			title: data.name.full,
			url: data.url,
		}))
			.pipe(SearchResultSchema)
			.safeParse(performer),
	);
}

export function transformVenueToSearchResult(venue: Venue) {
	return transform<Venue>(() =>
		VenueSchema.transform((data) => ({
			assets: data.assets,
			subtitle: [data.city, data.state].filter(Boolean).join(', '),
			title: data.name,
			url: data.url,
		}))
			.pipe(SearchResultSchema)
			.safeParse(venue),
	);
}
